import type DestinationSearchHit from "@travily/types/pending/models/DestinationSearchHit";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { analyticsEvents } from "@/lib/analytics-events";

// Ranking quality is read off the first few screens; past this the tail of an
// infinite scroll would dominate the payload without informing any metric.
const MAX_REPORTED_POSITION = 60;

// Enough to say what put a result where it is without shipping seventeen
// factors per hit for sixty hits. The keys are ordered by contribution, so the
// first few are the answer and the rest are the long tail of a weighted mean.
const REPORTED_FACTORS = 3;

// What a relevance metric needs to attribute a result: the score it was
// ordered by, and what produced it. Absent for anything the cluster ordered
// itself — a price sort, the map feed — which is exactly when there is no
// ranking to attribute.
function attribution(hit: DestinationSearchHit) {
  const ranking = hit._travily_ranking;
  if (!ranking) return {};

  return {
    composite_score: ranking.score,
    coverage: ranking.coverage,
    top_factors: ranking.factors.slice(0, REPORTED_FACTORS).map((factor) => factor.key),
  };
}

interface UseDestinationAnalyticsOptions {
  effectiveMonths: number[];
  hits: DestinationSearchHit[];
  isLoading: boolean;
  /** The version the page was rendered with. Only reported when the results
   *  themselves carry none — a price sort, or an empty list. */
  rankingVersion: string;
  selectedBands: string[];
  sort: string;
  totalCount: number;
}

// Every ranked hit in one response carries the same version, so the first that
// has one answers for the response. The page prop is the fallback rather than
// the source: it is fixed at page load, and this session may have re-ranked
// several times since without navigating.
function servedVersion(hits: DestinationSearchHit[], fallback: string): string {
  return hits.find((hit) => hit._travily_ranking)?._travily_ranking?.version ?? fallback;
}

export function useDestinationAnalytics({
  effectiveMonths,
  hits,
  isLoading,
  rankingVersion,
  selectedBands,
  sort,
  totalCount,
}: UseDestinationAnalyticsOptions) {
  const posthog = usePostHog();

  // Memoised on the resolved STRING, not on `hits`: every scroll page carries
  // the same version, so the context stays referentially stable and the search
  // key below does not churn. A version that genuinely changes mid-session is a
  // different ranking, and re-reporting it from the top is the point.
  const served = servedVersion(hits, rankingVersion);

  const context = useMemo(
    () => ({
      ranking_version: served,
      sort,
      effective_months: effectiveMonths,
      selected_bands: selectedBands,
    }),
    [served, sort, effectiveMonths, selectedBands],
  );

  // Identity of the SEARCH, not of its results: loading another scroll page must
  // report only the positions it added, while changing the window or the sort is
  // a different ranking and reports again from the top.
  const searchKey = JSON.stringify(context);
  const reported = useRef({ searchKey: "", count: 0 });

  useEffect(() => {
    if (reported.current.searchKey !== searchKey) reported.current = { searchKey, count: 0 };
    // Hits survive a pending search so results don't flicker, so reporting mid-flight
    // would attribute the outgoing ranking's list to the incoming one.
    if (isLoading) return;

    const from = reported.current.count;
    const shown = hits.slice(from, MAX_REPORTED_POSITION);
    if (shown.length === 0) return;

    reported.current.count = from + shown.length;
    posthog.capture(analyticsEvents.destinationResultsViewed, {
      ...context,
      total_count: totalCount,
      from_position: from + 1,
      // Kept alongside `results` because existing queries read it, and it is
      // the one property that survives a sort the ranking did not order.
      destination_ids: shown.map((hit) => hit.id),
      results: shown.map((hit) => ({ id: hit.id, ...attribution(hit) })),
    });
  }, [context, hits, isLoading, posthog, searchKey, totalCount]);

  const trackResultClicked = useCallback(
    (hit: DestinationSearchHit, position: number) => {
      posthog.capture(analyticsEvents.destinationResultClicked, {
        ...context,
        destination_id: hit.id,
        destination_slug: `${hit.country_slug}/${hit.slug}`,
        position,
        ...attribution(hit),
      });
    },
    [context, posthog],
  );

  const trackResultSaved = useCallback(
    (hit: DestinationSearchHit, position: number, saved: boolean) => {
      posthog.capture(analyticsEvents.destinationResultSaved, {
        ...context,
        destination_id: hit.id,
        destination_slug: `${hit.country_slug}/${hit.slug}`,
        position,
        saved,
        ...attribution(hit),
      });
    },
    [context, posthog],
  );

  return { trackResultClicked, trackResultSaved };
}
